import { z } from 'zod'

export const AthenaPatientSchema = z.object({
  patient_id: z.string().nullable().optional(),
  athena_tablespace: z.string().nullable().optional(),
  full_name: z.string(),
  date_of_birth: z.string(),
  phone: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  insurance_provider: z.string().nullable().optional(),
  insurance_id: z.string().nullable().optional(),
  emr_link: z.string().nullable().optional(),
  match_found: z.boolean().optional(),
})

export type AthenaPatient = z.infer<typeof AthenaPatientSchema>

export const AvailableSlotSchema = z.object({
  start_time: z.string(),
  tablespace: z.string(),
  id: z.string(),
  physician_id: z.string(),
})

export type AvailableSlot = z.infer<typeof AvailableSlotSchema>

export const PracticeSchema = z.object({
  id: z.string(),
  name: z.string(),
  location: z.string(),
  type: z.enum(['hospital', 'clinic', 'specialty']).optional(),
  phone: z.string().nullable().optional(),
  backline: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  is_active: z.boolean().optional(),
  timezone: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
})

export type Practice = z.infer<typeof PracticeSchema>

export const ProviderSearchResultSchema = z.object({
  id: z.string(),
  external_id: z.string().nullable().optional(),
  tablespace: z.string().nullable().optional(),
  practice_id: z.string().nullable().optional(),
  hospital_follow_up_appointment_type: z.string().nullable().optional(),
  name: z.string(),
  nickname: z.string().nullable().optional(),
  is_employed: z.boolean(),
  accepting_new_patients: z.boolean().nullable().optional(),
  phone: z.string().nullable().optional(),
  standing_order: z.string().nullable().optional(),
  available_slots_in_window: z.array(AvailableSlotSchema).default([]),
  first_available_slot_outside_window: AvailableSlotSchema.nullable().optional(),
  is_patient_pcp: z.boolean().optional(),
  distance_from_patient_miles: z.number().nullable().optional(),
})

export type ProviderSearchResult = z.infer<typeof ProviderSearchResultSchema>

export const AlreadyScheduledSchema = z.object({
  appointment_type: z.string(),
  start: z.string(),
  physician_name: z.string(),
  facility_name: z.string(),
})

export type AlreadyScheduled = z.infer<typeof AlreadyScheduledSchema>

export const ProviderSearchResponseSchema = z.object({
  pac_phone: z.string().nullable().optional(),
  providers: z.array(ProviderSearchResultSchema),
  practices: z.array(PracticeSchema),
  is_new_patient: z.boolean().nullable().optional(),
  already_scheduled: AlreadyScheduledSchema.nullable().optional(),
})

export type ProviderSearchResponse = z.infer<typeof ProviderSearchResponseSchema>

export const DirectPatientResponseSchema = AthenaPatientSchema.extend({
  hospital_id: z.string(),
})

export type DirectPatientResponse = z.infer<typeof DirectPatientResponseSchema>

export const GenericResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
})

export type GenericResponse = z.infer<typeof GenericResponseSchema>
